import { createRouter, createWebHistory } from 'vue-router'
import { setSessionTimerByRemainingSeconds } from '@/composables/useSessionTimer'
import { getAccessTokenInfoCheck } from '@/service/auth.service'
import {
  handleSessionExpiredOnce,
  isDuplicateLoginInfo,
  isTokenExpiredInfo,
  isUnauthorizedError
} from '@/utils/sessionExpiry'
import { useUserStore } from '@/store/user.store'
import exprRoutes from '@/routes/expr'
import commonRoutes from '@/routes/common'
import playRoutes from '@/routes/play'
import spfcRoutes from '@/routes/spfc'
import lectRoutes from '@/routes/lect'
import campRoutes from '@/routes/camp'
import myRsvtRouts from '@/routes/myRsvt'
import myInfoEdits from '@/routes/myInfoEdit'
import changePasswordRoutes from '@/routes/changePassword'
import withdrawRoutes from '@/routes/withdraw'

const routes = [
  {
    path: '/',
    name: 'Main',
    meta: { layout: 'main' },
    component: () => import('@/pages/main/Main.vue')
  },
  {
    path: '/login',
    name: 'Login',
    meta: { layout: 'default' },
    component: () => import('@/pages/login/Login.vue')
  },
  {
    path: '/identity-verify',
    name: 'IdentityVerify',
    meta: { layout: 'default' },
    component: () => import('@/pages/identity/IdentityVerify.vue')
  },
  {
    path: '/integration',
    redirect: { name: 'IntgAcctList' },
    meta: { layout: 'default' },
    children: [
      {
        path: '',
        name: 'IntgAcctList',
        meta: { layout: 'default' },
        component: () => import('@/pages/integration/IntgAcctList.vue')
      },
      {
        path: 'select',
        name: 'IntgAcctSelect',
        meta: { layout: 'default' },
        component: () => import('@/pages/integration/IntgAcctSelect.vue')
      },
      {
        path: 'verify',
        name: 'IntgAcctVerify',
        meta: { layout: 'default' },
        component: () => import('@/pages/integration/IntgAcctVerify.vue')
      },
      {
        path: 'complete',
        name: 'IntgAcctComplete',
        meta: { layout: 'default' },
        component: () => import('@/pages/integration/IntgAcctComplete.vue')
      }
    ]
  },
  {
    path: '/join',
    redirect: { name: 'JoinIntro' },
    meta: { layout: 'default' },
    children: [
      {
        path: '',
        name: 'JoinIntro',
        meta: { layout: 'default', menu: 'join' },
        component: () => import('@/pages/join/Intro.vue')
      },
      {
        path: 'auth',
        name: 'JoinAuth',
        meta: { layout: 'default', menu: 'join' },
        component: () => import('@/pages/join/Step1.vue')
      },
      {
        path: 'step2',
        name: 'JoinStep2',
        meta: { layout: 'default', menu: 'join' },
        component: () => import('@/pages/join/Step2.vue')
      },
      {
        path: 'step3',
        name: 'JoinStep3',
        meta: { layout: 'default', menu: 'join' },
        component: () => import('@/pages/join/Step3.vue')
      },
      {
        path: 'step3-kids',
        name: 'JoinStep3Kids',
        meta: { layout: 'default', menu: 'join' },
        component: () => import('@/pages/join/Step3Kids.vue')
      },
      {
        path: 'complete',
        name: 'JoinComplete',
        meta: { layout: 'default', menu: 'join' },
        component: () => import('@/pages/join/Step4.vue')
      }
    ]
  },
  {
    path: '/find',
    redirect: { name: 'FindMain' },
    meta: { layout: 'default' },
    children: [
      {
        path: '',
        name: 'FindMain',
        meta: { layout: 'default', menu: 'join' },
        component: () => import('@/pages/find/FindMain.vue')
      },
      {
        path: 'id/auth',
        name: 'FindIdAuth',
        meta: { layout: 'default', menu: 'join' },
        component: () => import('@/pages/find/FindIdAuth.vue')
      },
      {
        path: 'id/result',
        name: 'FindIdResult',
        meta: { layout: 'default', menu: 'join' },
        component: () => import('@/pages/find/FindIdResult.vue')
      },
      {
        path: 'pw/input',
        name: 'FindPwInput',
        meta: { layout: 'default', menu: 'join' },
        component: () => import('@/pages/find/FindPwInput.vue')
      },
      {
        path: 'pw/auth',
        name: 'FindPwAuth',
        meta: { layout: 'default', menu: 'join' },
        component: () => import('@/pages/find/FindPwAuth.vue')
      },
      {
        path: 'pw/change',
        name: 'FindPwChange',
        meta: { layout: 'default', menu: 'join' },
        component: () => import('@/pages/find/FindPwChange.vue')
      },
      {
        path: 'pw/result',
        name: 'FindPwResult',
        meta: { layout: 'default', menu: 'join' },
        component: () => import('@/pages/find/FindPwResult.vue')
      }
    ]
  },
  ...exprRoutes,
  ...commonRoutes,
  ...playRoutes,
  ...spfcRoutes,
  ...lectRoutes,
  ...campRoutes,
  ...myRsvtRouts,
  ...myInfoEdits,
  ...changePasswordRoutes,
  ...withdrawRoutes,
  {
    path: '/inst-utztn-guide/list',
    name: 'InstUtztnGuideList',
    meta: { layout: 'default' },
    component: () => import('@/pages/instUtztnGuide/InstUtztnGuideView.vue')
  },
  {
    path: '/search',
    name: 'SearchResult',
    meta: { layout: 'default' },
    component: () => import('@/pages/search/SearchResultView.vue')
  },
  {
    path: '/popup/duplicateId',
    name: 'CheckDuplicateIdPopup',
    meta: { layout: 'blank' },
    component: () => import('@/pages/popup/CheckDuplicateIdPopup.vue')
  },
  {
    path: '/popup/nontact-auth',
    name: 'NontactAuthPopup',
    meta: { layout: 'blank' },
    component: () => import('@/pages/popup/NontactAuthPopup.vue')
  },
  {
    path: '/popup/juso-address',
    name: 'JusoAddressPopup',
    meta: { layout: 'blank' },
    component: () => import('@/pages/popup/JusoAddressPopup.vue')
  },
  {
    path: '/notFound',
    name: 'NotFound',
    meta: { layout: 'blank' },
    component: () => import('@/pages/NotFound.vue')
  },
  {
    path: '/actuator/health',
    name: 'Health',
    meta: { layout: 'blank' },
    component: () => import('@/pages/Health.vue')
  },
  {
    path: '/netfunnel-test',
    redirect: { name: 'NetFunnelStart' },
    meta: { layout: 'blank' },
    children: [
      {
        path: 'start',
        name: 'NetFunnelStart',
        meta: { layout: 'blank', menu: 'test' },
        component: () => import('@/pages/test/NetFunnelStartView.vue')
      },
      {
        path: 'end',
        name: 'NetFunnelEnd',
        meta: { layout: 'blank', menu: 'test' },
        component: () => import('@/pages/test/NetFunnelEndView.vue')
      },
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/notFound'
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

const SESSION_SYNC_AT_KEY = 'sessionSyncAt'
const SESSION_SYNC_THROTTLE_MS = 2000
let lastSyncedAccessToken = ''

const shouldSyncSession = (accessToken) => {
  const token = String(accessToken || '')
  if (!token || token !== lastSyncedAccessToken) {
    lastSyncedAccessToken = token
    sessionStorage.setItem(SESSION_SYNC_AT_KEY, String(Date.now()))
    return true
  }
  const last = Number(sessionStorage.getItem(SESSION_SYNC_AT_KEY) || 0)
  const now = Date.now()
  if (now - last < SESSION_SYNC_THROTTLE_MS) return false
  sessionStorage.setItem(SESSION_SYNC_AT_KEY, String(now))
  return true
}

const syncSessionFromServer = async (accessToken) => {
  if (!accessToken) return { expired: false }
  if (!shouldSyncSession(accessToken)) return { expired: false }

  try {
    console.info('[session] sync request', { hasToken: true })

    const info = await getAccessTokenInfoCheck({
      accessToken,
      refreshMode: 'AUTO'
    })

    const remainSeconds = info?.remainingSeconds ?? null
    const raw = info?.raw

    if (raw && typeof raw === 'object') {
      console.info('[session] sync response', {
        displayRemainingSeconds: raw.displayRemainingSeconds ?? raw?.data?.displayRemainingSeconds,
        remainingSeconds: raw.remainingSeconds ?? raw?.data?.remainingSeconds,
        serverRemainingSeconds: raw.serverRemainingSeconds ?? raw?.data?.serverRemainingSeconds,
        refreshApplied: raw.refreshApplied ?? raw?.data?.refreshApplied,
        resultCode: raw.resultCode ?? raw?.data?.resultCode ?? raw.code ?? raw?.data?.code,
        resultMessage: raw.resultMessage ?? raw?.data?.resultMessage ?? raw.message ?? raw?.data?.message,
        logoutReason: raw.logoutReason ?? raw?.data?.logoutReason ?? raw.reason ?? raw?.data?.reason,
        status: info?.status
      })
    } else {
      console.info('[session] sync response', { remainSeconds, status: info?.status })
    }

    if (isDuplicateLoginInfo(info)) {
      return { expired: true, payload: info }
    }

    if (remainSeconds !== null) {
      setSessionTimerByRemainingSeconds(remainSeconds)
    }

    if (isTokenExpiredInfo(info)) {
      return { expired: true, payload: info }
    }
  } catch (e) {
    if (isUnauthorizedError(e)) {
      return { expired: true, payload: e?.response?.data ?? e }
    }
  }

  return { expired: false }
}

router.beforeEach(async (to, from, next) => {
  console.info('[route] beforeEach', { to: to.path })

  if (to.path === '/login') {
    const redirectRaw = Array.isArray(to.query?.redirect)
      ? String(to.query.redirect[0] || '')
      : String(to.query?.redirect || '')
    const redirect = decodeURIComponent(redirectRaw || '')
    if (redirect.startsWith('/login')) {
      next({ path: '/login', query: {} })
      return
    }
    next()
    return
  }

  const userStore = useUserStore()
  const accessToken = sessionStorage.getItem('accessToken') || userStore.accessToken

  if (accessToken && !sessionStorage.getItem('accessToken')) {
    sessionStorage.setItem('accessToken', accessToken)
  }

  if (to.meta.requiresAuth && !accessToken) {
    next({
      path: '/login',
      query: { redirect: to.fullPath }
    })
    return
  }

  if (accessToken) {
    const skipAt = Number(sessionStorage.getItem('skip_session_sync_once') || 0)
    if (skipAt && Date.now() - skipAt < 10_000) {
      sessionStorage.removeItem('skip_session_sync_once')
    } else {
      sessionStorage.removeItem('skip_session_sync_once')
      const sync = await syncSessionFromServer(accessToken)
      if (sync?.expired) {
        handleSessionExpiredOnce(sync?.payload ?? '/login', '/login')
        next(false)
        return
      }
    }
  }

  document.querySelector('.pop_logout')?.classList.remove('on')

  if (accessToken) {
    const identityVerified = sessionStorage.getItem('identityVerifiedMember')
    const allowedPaths = ['/login', '/identity-verify', '/integration']
    const isAllowed = allowedPaths.some((p) => to.path === p || to.path.startsWith(p + '/'))

    if (identityVerified === 'false' && !isAllowed) {
      next({ name: 'IdentityVerify' })
      return
    }
  }

  next()
})

export default router
